import {useRef} from 'react';
import {observer} from 'mobx-react';
import {FormattedMessage} from 'react-intl';
import moment from 'moment';
import _ from 'lodash';
import {EmployeeNameWithAvatar} from 'shared/components';
import ActionLinks from 'main/components/tables/components/ActionLinks';
import TableOverlay from 'main/components/tables/components/TableOverlay';
import ShiftCell from './ShiftCell';

const DayHeaders = observer(({uiState}) => {
  return (
    <div className='ShiftGrid-row'>
      <div className='ShiftGrid-corner'/>
      {uiState.days.map(day => (
        <div key={day.format()} className={`ShiftGrid-dayHeader ${day.isSame(moment(), 'day') ? 'ShiftGrid-dayHeader--today' : ''}`}>
          <div className='small'>{day.format('ddd')}</div>
          <div className='h3 medium'>{day.format('D')}</div>
        </div>
      ))}
    </div>
  );
});

const Hours = ({hours}) => <FormattedMessage id='time_tracking.shifts.SCHEDULED_HOURS' values={{hours: _.round(hours, 2)}}/>;

const ScheduledHours = observer(({uiState, employeeId}) => {
  return <Hours hours={uiState.scheduledHoursFor(employeeId)}/>;
});

const UnassignedShiftsRow = observer(({uiState}) => {
  return (
    <div className='ShiftGrid-row ShiftGrid-row--unassigned'>
      <div className='ShiftGrid-rowHeader'>
        <div className='h4 medium'><FormattedMessage id='time_tracking.shifts.Unassigned Shifts'/></div>
        <div className='jumbo'><ScheduledHours uiState={uiState} employeeId={null}/></div>
      </div>
      {uiState.days.map(day => <ShiftCell key={day.format()} uiState={uiState} employee={null} day={day}/>)}
    </div>
  );
});

const EmployeeRow = observer(({uiState, employee}) => {
  return (
    <div className='ShiftGrid-row'>
      <div className='ShiftGrid-rowHeader flex align-items-middle justify-between'>
        <div className='ShiftGrid-employee'>
          <EmployeeNameWithAvatar
            employee={employee}
            size='sm'
            details={<ScheduledHours uiState={uiState} employeeId={employee.id}/>}
          />
        </div>
        <ActionLinks model={employee} onlyShowCustomLinks customLinks={uiState.employeeActionLinks(employee)}/>
      </div>
      {uiState.days.map(day => <ShiftCell key={day.format()} uiState={uiState} employee={employee} day={day}/>)}
    </div>
  );
});

// The sticky section and the vertically scrolling section can't share horizontal scrollbars, but we need to keep their horizontal scroll in sync
function syncHorizontalScroll(source, target) {
  if (target.current.scrollLeft !== source.current.scrollLeft) {
    target.current.scrollLeft = source.current.scrollLeft;
  }
}

const TotalsRow = observer(({uiState}) => {
  return (
    <div className='ShiftGrid-row ShiftGrid-row--totals'>
      <div className='ShiftGrid-totalsHeader'>
        <span className='medium'><FormattedMessage id='time_tracking.shifts.Assigned Total'/></span>
        <Hours hours={uiState.assignedHours}/>
      </div>
      {uiState.days.map(day => (
        <div key={day.format()} className='ShiftGrid-totalsCell'>
          <Hours hours={uiState.assignedHoursOn(day)}/>
        </div>
      ))}
    </div>
  );
});

const ShiftGrid = observer(({uiState}) => {
  const pinnedRef = useRef(null);
  const employeesRef = useRef(null);

  return (
    <div className='ShiftGrid'>
      <div className='ShiftGrid-pinned'>
        <div
          ref={pinnedRef}
          className='ShiftGrid-scroll ShiftGrid-scroll--hiddenScrollbar'
          onScroll={() => syncHorizontalScroll(pinnedRef, employeesRef)}
        >
          <DayHeaders uiState={uiState}/>
          <UnassignedShiftsRow uiState={uiState}/>
        </div>
      </div>
      <div ref={employeesRef} className='ShiftGrid-scroll' onScroll={() => syncHorizontalScroll(employeesRef, pinnedRef)}>
        {uiState.schedule.sortedEmployees.map(employee => <EmployeeRow key={employee.id} uiState={uiState} employee={employee}/>)}
        <TotalsRow uiState={uiState}/>
      </div>
      <TableOverlay isUpdating={uiState.isUpdating}/>
    </div>
  );
});

export default ShiftGrid;
